import { useState } from "react";
import {
  NotAuthenticatedError,
  api,
  type Promotion,
  type PromotionList,
} from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

/**
 * Promotions: the page where a change reaches production, or does not.
 *
 * The operating model, in the operator's own words: *"ให้ AI Agent เสนอขึ้นมาให้ผม
 * ในระบบ แล้วให้ผมกด ยืนยันอนุมัติ"* — an agent proposes, one person approves.
 *
 * Until this page existed the gate was shut rather than strict. Six endpoints
 * enforced it server-side and nothing rendered them, so the only way to approve
 * anything was curl. Worse, `POST /promotions` records the session's own user
 * as proposer and nobody may approve their own proposal, so a lone operator
 * could not approve at all. The **Propose via agent** form is the path that
 * works: the person supplies the policy, the agent reviews it and proposes, and
 * the person is then free to approve.
 *
 * Three rules it follows, all from the same idea as Risk Center:
 *
 * 1. **The server decides, the page reports.** `can_activate`,
 *    `approvals_valid` and `approvals_required` are read from the response, not
 *    recomputed here. A second implementation of the gate in TypeScript is a
 *    second thing that can be wrong about whether a change is allowed.
 * 2. **An approval that no longer applies is shown as such.** A request can
 *    carry an approval and still not be approvable, because approvals bind to a
 *    content hash. That difference is the entire point of binding them, so it
 *    is on the screen rather than folded into a count.
 * 3. **Activation is its own button.** Approving and activating are separate
 *    acts (CLAUDE.md rule 15), so they are separate clicks — not one button
 *    that does both because it is convenient.
 */
export function Promotions({
  onSignedOut,
  canPropose,
  canApprove,
  canActivate,
}: {
  onSignedOut: () => void;
  canPropose: boolean;
  canApprove: boolean;
  canActivate: boolean;
}) {
  const { data, error, loading, reload } = useApiData<PromotionList>(
    () => api.promotions(),
    onSignedOut,
  );

  const pending = (data?.promotions ?? []).filter(
    (p) => p.state === "PENDING_APPROVAL",
  );
  const rest = (data?.promotions ?? []).filter(
    (p) => p.state !== "PENDING_APPROVAL",
  );

  return (
    <>
      <h1>Promotions</h1>
      <p className="subtitle">
        Nothing reaches production without passing through here.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      {canPropose && <ProposeViaAgent onProposed={reload} onSignedOut={onSignedOut} />}

      <PageState
        loading={loading}
        error={error}
        empty={(data?.promotions ?? []).length === 0}
        emptyMessage="No change has been proposed yet."
      >
        {pending.length > 0 && (
          <>
            <h2>Waiting for you</h2>
            {pending.map((promotion) => (
              <PromotionCard
                key={promotion.request_id}
                promotion={promotion}
                canApprove={canApprove}
                canActivate={canActivate}
                onChanged={reload}
                onSignedOut={onSignedOut}
              />
            ))}
          </>
        )}

        {rest.length > 0 && (
          <>
            <h2>History</h2>
            {rest.map((promotion) => (
              <PromotionCard
                key={promotion.request_id}
                promotion={promotion}
                canApprove={canApprove}
                canActivate={canActivate}
                onChanged={reload}
                onSignedOut={onSignedOut}
              />
            ))}
          </>
        )}
      </PageState>
    </>
  );
}

const STATE_BADGE: Record<string, string> = {
  PENDING_APPROVAL: "badge",
  APPROVED: "badge ok",
  ACTIVE: "badge ok",
  REJECTED: "badge danger",
  ROLLED_BACK: "badge danger",
  SUPERSEDED: "badge",
  DRAFT: "badge",
};

function PromotionCard({
  promotion,
  canApprove,
  canActivate,
  onChanged,
  onSignedOut,
}: {
  promotion: Promotion;
  canApprove: boolean;
  canActivate: boolean;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const proposedByAgent = promotion.proposed_by.startsWith("agent:");
  const staleApproval = promotion.approvals.some(
    (a) => !a.applies_to_current_content,
  );

  return (
    <div className="card">
      <h2>
        {promotion.kind} → {promotion.target_version}{" "}
        <span className={STATE_BADGE[promotion.state] ?? "badge"}>
          {promotion.state}
        </span>
      </h2>

      <div className="metrics">
        <div className="metric">
          <span className="label">Proposed by</span>
          <span className="value">{promotion.proposed_by}</span>
        </div>
        <div className="metric">
          <span className="label">Proposed</span>
          <span className="value">{formatTime(promotion.proposed_at)}</span>
        </div>
        <div className="metric">
          <span className="label">Approvals</span>
          <span className="value">
            {promotion.approvals_valid} / {promotion.approvals_required}
          </span>
        </div>
        <div className="metric">
          <span className="label">Content hash</span>
          <span className="value mono">
            {promotion.content_hash.slice(0, 12)}
          </span>
        </div>
      </div>

      <p>{promotion.reason}</p>

      {staleApproval && (
        <div className="notice error">
          An approval on this request was given for different content and no
          longer counts. Approvals bind to the content hash on purpose — an
          approval that survived an edit would be an approval of something
          nobody read.
        </div>
      )}

      {promotion.evidence_refs.length > 0 ? (
        <p className="dim">
          Evidence:{" "}
          {promotion.evidence_refs.map((ref) => (
            <span key={ref} className="mono">
              {ref}{" "}
            </span>
          ))}
          — open the Agent Control Room to read the report behind this request.
        </p>
      ) : (
        <p className="dim">No evidence attached.</p>
      )}

      {promotion.approvals.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Approver</th>
              <th>When</th>
              <th>Reason</th>
              <th>Still applies</th>
            </tr>
          </thead>
          <tbody>
            {promotion.approvals.map((approval) => (
              <tr key={`${approval.approver}-${approval.approved_at}`}>
                <td>{approval.approver}</td>
                <td>{formatTime(approval.approved_at)}</td>
                <td>{approval.reason}</td>
                <td>
                  {approval.applies_to_current_content ? (
                    "yes"
                  ) : (
                    <span className="badge danger">no</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {promotion.state === "PENDING_APPROVAL" && canApprove && (
        <Decide
          promotion={promotion}
          proposedByAgent={proposedByAgent}
          onChanged={onChanged}
          onSignedOut={onSignedOut}
        />
      )}

      {promotion.can_activate && canActivate && (
        <Activate
          promotion={promotion}
          onChanged={onChanged}
          onSignedOut={onSignedOut}
        />
      )}

      {promotion.state === "ACTIVE" && canActivate && (
        <RollBack
          promotion={promotion}
          onChanged={onChanged}
          onSignedOut={onSignedOut}
        />
      )}

      {promotion.activated_at && (
        <p className="dim">Activated {formatTime(promotion.activated_at)}</p>
      )}
    </div>
  );
}

function Decide({
  promotion,
  proposedByAgent,
  onChanged,
  onSignedOut,
}: {
  promotion: Promotion;
  proposedByAgent: boolean;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function act(which: "approve" | "reject") {
    setBusy(true);
    setMessage(null);
    try {
      if (which === "approve") {
        await api.approvePromotion(promotion.request_id, reason);
      } else {
        await api.rejectPromotion(promotion.request_id, reason);
      }
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="reason (required)"
        aria-label={`Reason for deciding ${promotion.request_id}`}
      />
      <button
        disabled={busy || reason.trim() === ""}
        onClick={() => act("approve")}
      >
        {busy ? "Working…" : "Approve"}
      </button>
      <button
        className="danger"
        disabled={busy || reason.trim() === ""}
        onClick={() => act("reject")}
      >
        Reject
      </button>
      {message && <div className="notice error">{message}</div>}
      <p className="dim">
        {proposedByAgent
          ? "Proposed by an agent, so you may approve it. Read the evidence first — the report is the only independent look this change gets."
          : "Proposed by a person. If that person is you, the server will refuse: nothing may approve its own proposal. Use Propose via agent instead."}
      </p>
    </>
  );
}

function Activate({
  promotion,
  onChanged,
  onSignedOut,
}: {
  promotion: Promotion;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function activate() {
    setBusy(true);
    setMessage(null);
    try {
      await api.activatePromotion(promotion.request_id);
      setConfirming(false);
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="notice">
        Approved. <strong>Activation is a separate act</strong> — this is the
        moment production changes.
      </div>
      {confirming ? (
        <>
          <button disabled={busy} onClick={activate}>
            {busy ? "Activating…" : `Yes, activate ${promotion.target_version}`}
          </button>
          <button disabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button onClick={() => setConfirming(true)}>Activate…</button>
      )}
      {message && <div className="notice error">{message}</div>}
    </>
  );
}

function RollBack({
  promotion,
  onChanged,
  onSignedOut,
}: {
  promotion: Promotion;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function rollBack() {
    setBusy(true);
    setMessage(null);
    try {
      await api.rollbackPromotion(promotion.request_id, reason);
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="reason for rolling back (required)"
        aria-label={`Reason for rolling back ${promotion.request_id}`}
      />
      <button
        className="danger"
        disabled={busy || reason.trim() === ""}
        onClick={rollBack}
      >
        {busy ? "Rolling back…" : "Roll back"}
      </button>
      {message && <div className="notice error">{message}</div>}
    </>
  );
}

/**
 * Ask the reviewing agent to produce evidence and propose the change.
 *
 * The policy is pasted rather than edited field by field, deliberately for now:
 * a form that reconstructs a risk policy is a second place the policy's shape
 * is defined, and the two drifting apart is how a limit gets silently dropped.
 * The server loads whatever is pasted through the real `RiskPolicy` loader and
 * refuses it here if it will not load, so a bad paste fails at submission
 * rather than at activation.
 */
function ProposeViaAgent({
  onProposed,
  onSignedOut,
}: {
  onProposed: () => void;
  onSignedOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [targetVersion, setTargetVersion] = useState("");
  const [reason, setReason] = useState("");
  const [policyJson, setPolicyJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [declined, setDeclined] = useState<string | null>(null);
  const [review, setReview] = useState<Record<string, unknown> | null>(null);

  async function submit() {
    setBusy(true);
    setMessage(null);
    setDeclined(null);
    setReview(null);

    let content: Record<string, unknown>;
    try {
      content = JSON.parse(policyJson);
    } catch (err) {
      setMessage(`That is not valid JSON: ${String(err)}`);
      setBusy(false);
      return;
    }

    try {
      const result = await api.proposeViaAgent({
        kind: "RISK_POLICY",
        target_version: targetVersion,
        reason,
        content,
      });
      setReview(result.review.output);
      if (result.proposed) {
        setPolicyJson("");
        setTargetVersion("");
        setReason("");
        onProposed();
      } else {
        // Not an error. A review that failed or could not be trusted is
        // exactly when nothing should be put in front of an approver.
        setDeclined(result.declined_reason);
      }
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="card">
        <h2>Propose a change</h2>
        <p className="dim">
          An agent reviews the change and proposes it; you approve. This is the
          only path that works for one operator — a proposal you made yourself
          is one you may not approve.
        </p>
        <button onClick={() => setOpen(true)}>Propose via agent…</button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Propose a risk policy change</h2>
      <p className="dim">
        Paste the complete policy, not just the changed lines. The diff against
        the running policy is computed on the server, and the direction of every
        limit — whether it loosens or tightens — is decided in code so the agent
        never infers it.
      </p>

      <input
        value={targetVersion}
        onChange={(e) => setTargetVersion(e.target.value)}
        placeholder="target version, e.g. 2.0.0"
        aria-label="Target version"
      />
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="why this change (required)"
        aria-label="Reason for the change"
      />
      <textarea
        value={policyJson}
        onChange={(e) => setPolicyJson(e.target.value)}
        placeholder='{"version": "2.0.0", "account": {…}, "limits": {…}, …}'
        aria-label="Policy JSON"
        rows={12}
      />

      <button
        disabled={
          busy ||
          targetVersion.trim() === "" ||
          reason.trim() === "" ||
          policyJson.trim() === ""
        }
        onClick={submit}
      >
        {busy ? "Asking the agent…" : "Ask the agent to review and propose"}
      </button>
      <button disabled={busy} onClick={() => setOpen(false)}>
        Cancel
      </button>

      {busy && (
        <p className="dim">
          The model scales to zero, so a cold start can take several minutes.
        </p>
      )}

      {declined && (
        <div className="notice error">
          <strong>Nothing was proposed.</strong> {declined}
        </div>
      )}

      {message && <div className="notice error">{message}</div>}

      {review && (
        <>
          <h3>Impact report</h3>
          <pre className="mono">{JSON.stringify(review, null, 2)}</pre>
        </>
      )}
    </div>
  );
}
